import { useCallback, useEffect, useMemo, useState } from "react";
import { spotifyApi } from "../lib/spotify";
import { useLocalFilesStore } from "../stores/localFilesStore";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { Track } from "../types";

interface SimilarityScore {
  track: Track;
  score: number;
}

export const useRelatedTracks = (currentTrack: Track | null, limit: number = 5) => {
  const [relatedTracks, setRelatedTracks] = useState<Track[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getAllAlbums } = useLocalLibraryStore();
  const { files: localFiles } = useLocalFilesStore();

  // Memoize all local tracks
  const allLocalTracks = useMemo(() => {
    return [...localFiles, ...getAllAlbums().flatMap((album) => album.tracks)];
  }, [localFiles, getAllAlbums]);

  const calculateSimilarity = useCallback((trackA: Track, trackB: Track): number => {
    let score = 0;

    // Same artist is a strong indicator
    if (trackA.artist.toLowerCase() === trackB.artist.toLowerCase()) {
      score += 0.5;
    }

    // Check artist name similarity (for featuring, remixes, etc.)
    if (
      trackA.artist.toLowerCase().includes(trackB.artist.toLowerCase()) ||
      trackB.artist.toLowerCase().includes(trackA.artist.toLowerCase())
    ) {
      score += 0.3;
    }

    // Compare genres if available
    const genresA = trackA.spotifyData?.genres || [];
    const genresB = trackB.spotifyData?.genres || [];
    const commonGenres = genresA.filter((g) => genresB.includes(g));
    score += commonGenres.length * 0.2;

    // Title similarity for remixes/versions
    if (
      trackA.title.toLowerCase().includes(trackB.title.toLowerCase()) ||
      trackB.title.toLowerCase().includes(trackA.title.toLowerCase())
    ) {
      score += 0.2;
    }

    return score;
  }, []);

  const fetchRelatedTracks = useCallback(async () => {
    if (!currentTrack) return;

    setIsLoading(true);
    try {
      const localSimilarities: SimilarityScore[] = allLocalTracks
        .filter((track) => track.id !== currentTrack.id)
        .map((track) => ({
          track,
          score: calculateSimilarity(currentTrack, track),
        }));

      // If track is from Spotify, also fetch Spotify recommendations
      let spotifyTracks: Track[] = [];
      if (currentTrack.source === "spotify" && currentTrack.spotifyData) {
        const recommendations = await spotifyApi.recommendations.get({
          seed_tracks: [currentTrack.id],
          limit: 10,
        });

        spotifyTracks = recommendations.tracks.map((track) => ({
          id: track.id,
          title: track.name,
          artist: track.artists[0].name,
          albumArt: track.album.images[0]?.url,
          duration: track.duration_ms / 1000,
          url: track.preview_url || "",
          source: "spotify" as const,
          spotifyData: track,
        }));
      }

      // Combine all tracks and remove duplicates based on track ID
      const uniqueSimilarities = Object.values(
        [
          ...localSimilarities,
          ...spotifyTracks.map((track) => ({
            track,
            score: calculateSimilarity(currentTrack, track),
          })),
        ].reduce((acc, curr) => {
          // Keep the entry with the highest score if duplicate ID exists
          if (!acc[curr.track.id] || acc[curr.track.id].score < curr.score) {
            acc[curr.track.id] = curr;
          }
          return acc;
        }, {} as Record<string, SimilarityScore>)
      );

      const sortedTracks = uniqueSimilarities
        .sort((a, b) => b.score - a.score)
        .map((item) => item.track)
        .slice(0, limit);

      setRelatedTracks(sortedTracks);
    } catch (error) {
      console.error("Error fetching related tracks:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentTrack, allLocalTracks, limit, calculateSimilarity]);

  useEffect(() => {
    fetchRelatedTracks();
  }, [fetchRelatedTracks]);

  return { relatedTracks, isLoading };
};
